import { CommonModule, DatePipe } from '@angular/common';
import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { MenuModule } from '@tbtk/menu';
import { KeycloakService } from 'keycloak-angular';
import { Subscription } from 'rxjs';
import { ModalComponent } from 'src/app/components/modal/modal.component';
import { UserModel } from 'src/app/models/profile.model';
import { StorageModel } from 'src/app/models/storage.model';
import { SharesService } from 'src/app/pages/shares/shares.service';
import { AuthStore } from 'src/app/services/auth.store';
import { CommonService } from 'src/app/services/common.service';
import { ModalService } from 'src/app/services/modal.service';
import { NotificationService } from 'src/app/services/notification.service';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore
import * as SgeCrypto from 'src/assets/lib/sge/sge-crypto';
@Component({
  selector: 'app-create-link-modal',
  standalone: true,
  providers: [ModalService, DatePipe],
  imports: [CommonModule, ModalComponent, FormsModule, ReactiveFormsModule, MenuModule, TranslateModule],
  templateUrl: './create-link-modal.component.html',
  styleUrls: ['./create-link-modal.component.scss'],
})
export class CreateLinkModalComponent implements OnDestroy, OnInit {
  @ViewChild('btnCancelRef') btnCancelRef!: ElementRef;
  modalTitle = this.commonService.getTranslatedMessage('CREATE_LINK');
  modalId = 'createLink';
  password = 'Paralo';
  ipAddress = '';
  shareName = '';
  selectedDateTime!: string;
  selectedItem: StorageModel = {} as StorageModel;
  timezone!: string;
  isInShares!: boolean;
  createLinkForm!: FormGroup;

  //subscription
  subscription: Subscription = new Subscription();
  userSubscription: Subscription = new Subscription();

  //data
  selectedItemList: any = [];
  objectIdList: any[] = [];
  user!: UserModel;
  objectIds?: string[];

  //boolean
  isEncrpyted = false;
  showError = false;

  constructor(
    private formBuilder: FormBuilder,
    private modalService: ModalService,
    private sharesService: SharesService,
    private datePipe: DatePipe,
    public commonService: CommonService,
    private authStore: AuthStore,
    private notificationService: NotificationService,
    private kcService: KeycloakService,
    private router: Router,
  ) {
    this.isInShares = this.router.url.startsWith('/paylasimlar');
    this.subscription.add(
      commonService.multipleSelectedItems$.subscribe((selectedItems) => {
        this.selectedItemList = selectedItems;
      }),
    );
    this.isEncrpyted = this.commonService.hasEncryptedObject(this.selectedItemList);
    this.userSubscription.add(
      authStore.user$.subscribe((user) => {
        this.user = user;
      }),
    );
    this.timezone = commonService.getCurrentTimezone();
    this.selectedDateTime = this.getOneMonthLaterDate();
    this.createForm();
    this.watchSwitchChanges();
  }

  ngOnInit(): void {
    if (this.isInShares) {
      this.commonService.sharesMultipleSelectedItems$.subscribe((item) => {
        this.selectedItemList = item;
        this.objectIds = item?.map((i: any) => i.objectId!);
      });
    } else {
      this.commonService.multipleSelectedItems$.subscribe((selectedItems) => {
        this.objectIds = selectedItems?.map((i) => i.objectId);
        this.selectedItem = {} as StorageModel;
        if (selectedItems.length === 1) {
          [this.selectedItem] = selectedItems;
        }
        this.isEncrpyted = this.commonService.hasEncryptedObject(selectedItems);
        this.createForm();
      });
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  createForm(): void {
    this.createLinkForm = this.formBuilder.group(
      {
        isDatePickerSwitchChecked: [true],
        selectedDateTime: [this.selectedDateTime, [Validators.required]],
        isPasswordSwitchChecked: [this.isEncrpyted],
        password: [
          null,
          this.isEncrpyted
            ? [
                Validators.required,
                Validators.minLength(8),
                Validators.pattern('(?=.*\\d)(?=.*[a-z])(?=.*[A-Z])(?!.*[^a-zA-Z0-9.\\-~!@#$%*()+=\\[\\]?])(?=.*[.\\-~!@#$%*()+=\\[\\]?]).*'),
              ]
            : [],
        ],
        passwordAgain: [
          null,
          this.isEncrpyted
            ? [
                Validators.required,
                Validators.minLength(8),
                Validators.pattern('(?=.*\\d)(?=.*[a-z])(?=.*[A-Z])(?!.*[^a-zA-Z0-9.\\-~!@#$%*()+=\\[\\]?])(?=.*[.\\-~!@#$%*()+=\\[\\]?]).*'),
              ]
            : [],
        ],
        isIpSwitchChecked: [false],
        ipAddress: ['', [Validators.maxLength(15), Validators.pattern('^(?!0)(?!.*\\.$)((1?\\d?\\d|25[0-5]|2[0-4]\\d)(\\.|$)){4}$')]],
        ipSelect: ['32'],
        isDownloadSwitchChecked: [false],
        downloadCount: null,
      },
      { validators: this.checkPasswordMatch },
    );
  }

  checkPasswordMatch(formGroup: FormGroup): void {
    const password = formGroup.get('password')?.value;
    const confirmPassword = formGroup.get('passwordAgain')?.value;

    if (password !== confirmPassword) {
      formGroup.get('passwordAgain')?.setErrors({ mismatch: true });
    } else {
      formGroup.get('passwordAgain')?.setErrors(null);
    }
  }

  collectObjectIds(arr: any[]): void {
    this.objectIdList = arr.filter((obj) => obj && obj.objectId !== undefined).map((obj) => obj.objectId);
  }

  onSubmit(): void {
    if (this.createLinkForm.valid) {
      const formValues = this.createLinkForm.value;
      // this.collectObjectIds(this.selectedItemList);
      const body = {
        objectIds: this.objectIds ?? [],
        password: formValues.password,
        allowIp: formValues.ipAddress || null,
        expireDate: this.datePipe.transform(formValues.selectedDateTime, "yyyy-MM-dd'T'HH:mm:ss.00" + this.timezone)!.toString(),
        downloadLimit: formValues.downloadCount ?? null,
      };

      this.subscription = this.sharesService.createPublicShare(body).subscribe({
        next: (res: any) => {
          const parentEncryptedId = this.selectedItemList[0]?.parentEncryptedId;
          const selectedEncryptedItemIdLists = this.commonService.collectEncryptedIds(this.selectedItemList);
          if (this.isEncrpyted) {
            this.kcService.getToken().then((token) => {
              SgeCrypto.createLinkSharing(token, parentEncryptedId, selectedEncryptedItemIdLists, res.shareId, formValues.password)
                .then(() => {
                  const notification = {
                    message: this.commonService.getTranslatedMessage('LINK_CREATED'),
                    actions: [{ label: this.commonService.getTranslatedMessage('LINK'), callback: (): void => this.onCopyShareLink(res), icon: 'ti ti-link' }],
                  };
                  this.notificationService.defaultNotification(notification);
                })
                .catch(() => {
                  const notification = {
                    message: this.commonService.getTranslatedMessage('LINK_CANNOT_CREATED'),
                  };
                  this.notificationService.errorNotification(notification);
                });
            });
          } else {
            this.onCopyShareDirectLink(res);
            const notification = {
              message: this.commonService.getTranslatedMessage('LINK_CREATED'),
              actions: [{ label: this.commonService.getTranslatedMessage('LINK'), callback: (): void => this.onCopyShareLink(res), icon: 'ti ti-link' }],
            };
            this.notificationService.defaultNotification(notification);
          }
          this.commonService.multipleSelectedItems$.next([this.selectedItem]);
        },
        complete: () => {
          this.createLinkForm.reset();
          this.keepFormDefaulValues();
          this.btnCancelRef.nativeElement.click();
          this.commonService.rebindListener.next();
        },
      });
    }
  }

  onCopyShareLink(item: any): void {
    const copyTextArea = document.createElement('textarea');
    copyTextArea.value = window.location.origin + '/baglanti-paylasimi/' + item.shareId;
    document.body.appendChild(copyTextArea);
    copyTextArea.select();
    this.copyToClipboard(copyTextArea.value);
    copyTextArea.remove();
    const notification = {
      message: this.commonService.getTranslatedMessage('LINK_COPIED'),
    };
    this.notificationService.successNotification(notification);
  }

  onCopyShareDirectLink(item: any): void {
    const copyTextArea = document.createElement('textarea');
    copyTextArea.value = window.location.origin + '/baglanti-paylasimi/' + item.shareId;
    document.body.appendChild(copyTextArea);
    copyTextArea.select();
    this.copyToClipboard(copyTextArea.value);
    copyTextArea.remove();
  }

  copyToClipboard(text: string): void {
    navigator.clipboard.writeText(text);
  }

  onCancel(): void {
    this.createLinkForm.reset();
    this.keepFormDefaulValues();
    this.btnCancelRef.nativeElement.click();
  }

  onSelectDateTime(event: any): void {
    const selectedDate = event.target.value;
    this.selectedDateTime = selectedDate;
  }

  getOneMonthLaterDate(): string {
    const currentDate = new Date();
    const oneMonthLater = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, currentDate.getDate());
    oneMonthLater.setHours(currentDate.getHours());
    oneMonthLater.setMinutes(currentDate.getMinutes());
    const formattedDate = this.datePipe.transform(oneMonthLater, "yyyy-MM-dd'T'HH:mm")!.toString();

    return formattedDate;
  }

  onInputValidation(): void {
    this.ipAddress = this.ipAddress.replace(/\D/g, '');

    if (this.ipAddress.length > 15) {
      this.ipAddress = this.ipAddress.substr(0, 15);
    }

    const parts = this.ipAddress.match(/.{1,3}/g) || [];
    parts.length = Math.min(parts.length, 4);
    const adjustedParts = parts.map((part, index) => {
      const digits = part.substr(0, 3);
      if (index === parts.length - 1) {
        const lastPart = digits.substr(0, 3);

        return Math.min(+lastPart, 255).toString();
      }

      if (+digits > 255) {
        return '255';
      }

      return digits;
    });
    this.ipAddress = adjustedParts.join('.');
  }

  closeModalButtonTrigger(): void {
    // Modalın kapatılmasıyla ilgili işlemleri burada gerçekleştirin
    this.modalService.closeModalManually(this.modalId);
    this.createLinkForm.reset();
    this.keepFormDefaulValues();
  }

  checkDate(selectedDate: string): void {
    const currentDate = new Date();
    const selectedDateTime = new Date(selectedDate);
    if (selectedDateTime <= currentDate || selectedDateTime < new Date()) {
      this.createLinkForm.get('selectedDateTime')?.setValue(this.getCurrentDateTime());
    }
    if (isNaN(selectedDateTime.getFullYear())) {
      this.createLinkForm.get('selectedDateTime')?.setValue(this.getCurrentDateTime());
    }
  }

  onInput(event: any): void {
    setTimeout(() => {
      const selectedDate = this.createLinkForm.get('selectedDateTime')?.value;
      this.checkDate(selectedDate);
    }, 1000);
  }

  getCurrentDateTime(): string {
    const currentDate = new Date();
    const oneMonthLater = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
    oneMonthLater.setHours(currentDate.getHours());
    oneMonthLater.setMinutes(currentDate.getMinutes() + 10);
    const formattedDate = this.datePipe.transform(oneMonthLater, "yyyy-MM-dd'T'HH:mm")!.toString();

    return formattedDate;
  }

  keepFormDefaulValues(): void {
    this.createLinkForm.patchValue({
      isDatePickerSwitchChecked: true,
      selectedDateTime: this.getOneMonthLaterDate(),
      isPasswordSwitchChecked: this.isEncrpyted,
      password: null,
      passwordAgain: null,
      isIpSwitchChecked: false,
      ipAddress: '',
      ipSelect: 32,
      isDownloadSwitchChecked: false,
      downloadCount: null,
    });
  }

  watchSwitchChanges(): void {
    this.createLinkForm.get('isPasswordSwitchChecked')?.valueChanges.subscribe((value) => {
      if (!value) {
        this.createLinkForm.get('password')?.reset();
        this.createLinkForm.get('passwordAgain')?.reset();
      }
    });

    this.createLinkForm.get('isIpSwitchChecked')?.valueChanges.subscribe((value) => {
      if (!value) {
        this.createLinkForm.get('ipAddress')?.reset();
        this.createLinkForm.get('ipSelect')?.reset();
      }
    });

    this.createLinkForm.get('isDownloadSwitchChecked')?.valueChanges.subscribe((value) => {
      if (!value) {
        this.createLinkForm.get('downloadCount')?.reset();
      }
    });
  }
}
